import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const ProfileSection = ({ onUpdate, onShowNotification }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [profileData, setProfileData] = useState({
    name: 'Alex Thompson',
    email: 'alex.thompson@example.com',
    bio: 'AI enthusiast and content creator specializing in prompt engineering and automation workflows.',
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1ca693890-1763293865080.png",
    avatarAlt: 'Professional headshot of a confident person with short brown hair wearing a navy blue blazer against a neutral gray background'
  });

  const [formData, setFormData] = useState(profileData);
  const [errors, setErrors] = useState({});

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData?.name?.trim()) {
      newErrors.name = 'Name is required';
    }
    if (!formData?.email?.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      newErrors.email = 'Invalid email format';
    }
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSave = () => {
    if (validateForm()) {
      setProfileData(formData);
      setIsEditing(false);
      onUpdate(formData);
      onShowNotification('success', 'Profile Updated', 'Your profile information has been saved successfully.');
    }
  };

  const handleCancel = () => {
    setFormData(profileData);
    setErrors({});
    setIsEditing(false);
  };

  const handleAvatarChange = (e) => {
    const file = e?.target?.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData((prev) => ({ ...prev, avatar: reader?.result }));
      };
      reader?.readAsDataURL(file);
    }
  };

  return (
    <div className="space-y-6 md:space-y-8">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl md:text-2xl font-semibold text-foreground">Account Information</h2>
          <p className="text-sm md:text-base text-muted-foreground mt-1">Manage your personal details and profile settings</p>
        </div>
        {!isEditing &&
        <Button variant="outline" iconName="Edit2" iconPosition="left" onClick={() => setIsEditing(true)}>
            Edit Profile
          </Button>
        }
      </div>
      <div className="bg-card rounded-lg border border-border p-6 md:p-8">
        <div className="flex flex-col md:flex-row gap-6 md:gap-8">
          <div className="flex flex-col items-center gap-4">
            <div className="relative">
              <div className="w-24 h-24 md:w-32 md:h-32 rounded-full overflow-hidden border-4 border-border">
                <Image
                  src={formData?.avatar}
                  alt={formData?.avatarAlt}
                  className="w-full h-full object-cover" />

              </div>
              {isEditing &&
              <label className="absolute bottom-0 right-0 w-10 h-10 bg-primary rounded-full flex items-center justify-center cursor-pointer hover:bg-primary/90 transition-colors">
                  <Icon name="Camera" size={20} color="#FFFFFF" />
                  <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleAvatarChange} />

                </label>
              }
            </div>
            {isEditing &&
            <p className="text-xs md:text-sm text-muted-foreground text-center">Click camera icon to upload new photo</p>
            }
          </div>

          <div className="flex-1 space-y-4 md:space-y-6">
            <Input
              label="Full Name"
              type="text"
              value={formData?.name}
              onChange={(e) => handleInputChange('name', e?.target?.value)}
              disabled={!isEditing}
              error={errors?.name}
              required
              placeholder="Enter your full name" />


            <Input
              label="Email Address"
              type="email"
              value={formData?.email}
              onChange={(e) => handleInputChange('email', e?.target?.value)}
              disabled={!isEditing}
              error={errors?.email}
              required
              placeholder="your.email@example.com" />


            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Bio</label>
              <textarea
                value={formData?.bio}
                onChange={(e) => handleInputChange('bio', e?.target?.value)}
                disabled={!isEditing}
                rows={4}
                className="w-full px-4 py-3 rounded-lg border border-border bg-input text-foreground disabled:opacity-60 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-ring resize-none text-sm md:text-base"
                placeholder="Tell us about yourself and your AI interests..." />

            </div>

            {isEditing &&
            <div className="flex flex-col sm:flex-row gap-3 pt-2">
                <Button variant="default" onClick={handleSave} iconName="Check" iconPosition="left" fullWidth>
                  Save Changes
                </Button>
                <Button variant="outline" onClick={handleCancel} fullWidth>
                  Cancel
                </Button>
              </div>
            }
          </div>
        </div>
      </div>
      <div className="bg-card rounded-lg border border-border p-6 md:p-8">
        <h3 className="text-lg md:text-xl font-semibold text-foreground mb-4 md:mb-6">Account Statistics</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {[
          { label: 'Prompts Created', value: '247', icon: 'Sparkles', color: 'var(--color-primary)' },
          { label: 'Favorites', value: '42', icon: 'Heart', color: 'var(--color-error)' },
          { label: 'Templates Used', value: '18', icon: 'FileText', color: 'var(--color-secondary)' },
          { label: 'Days Active', value: '89', icon: 'Calendar', color: 'var(--color-success)' }]?.
          map((stat) =>
          <div key={stat?.label} className="flex items-center gap-4 p-4 bg-muted rounded-lg">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${stat?.color}15` }}>
                <Icon name={stat?.icon} size={24} color={stat?.color} />
              </div>
              <div>
                <div className="text-2xl md:text-3xl font-bold text-foreground">{stat?.value}</div>
                <div className="text-xs md:text-sm text-muted-foreground">{stat?.label}</div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>);

};

export default ProfileSection;